import { createRouter, createWebHistory } from 'vue-router';
import RouteLocaleManager from './routeLocaleManager';
import LocaleManager from '@/i12e/i18n/localeManager';
import authService from '@/data/api/authService';
import RouteOutlet from '@/presentation/views/RouteOutlet.vue';
import { buildLoginRedirectQuery } from './authRedirect';

const routes = [
  {
    // Route path with optional locale parameter
    path: '/:locale?',
    component: RouteOutlet,
    beforeEnter: RouteLocaleManager.routeMiddleware,
    children: [
      // Child routes for the application, under the locale path
      {
        // Parent route for authentication views
        path: 'app/auth',
        component: () => import('@/presentation/views/AuthView.vue'),
        children: [
          {
            path: 'login',
            name: 'login',
            component: () =>
              import('@/presentation/components/auth/LoginWidget.vue')
          },
          {
            path: 'signup',
            name: 'signup',
            component: () =>
              import('@/presentation/components/auth/SignUpWidget.vue')
          },
          {
            path: '',
            redirect: `/${LocaleManager.activeLocale}/app/auth/login`
          }
        ]
      },
      {
        path: 'app',
        component: RouteOutlet,
        meta: { requiresAuth: false },
        children: [
          {
            path: 'pomodoro',
            name: 'pomodoro',
            component: () => import('@/presentation/views/PomodoroView.vue')
          }
        ]
      },
      {
        path: 'app',
        component: RouteOutlet,
        meta: { requiresAuth: true },
        children: [
          {
            path: 'todo',
            name: 'todo',
            component: () => import('@/presentation/views/ToDoView.vue')
          },
          {
            path: 'performance',
            name: 'performance',
            component: () =>
              import('@/presentation/views/PerformanceReport.vue')
          }
        ]
      },
      {
        path: '',
        redirect: (to) => {
          let locale = LocaleManager.activeLocale;
          if (LocaleManager.isLocaleSupported(to.params.locale)) {
            locale = to.params.locale;
          }
          return `/${locale}/app/auth`;
        }
      }
    ]
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: (_to, _, next) => {
      authService.logout();
      next({ path: '/' });
    }
  },
  {
    path: '/',
    redirect: `/${LocaleManager.activeLocale}/app/auth`
  }
];

// Create and export the Vue Router instance
const router = createRouter({
  // Use the Web History API for clean URLs
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

// Global beforeEach guard
router.beforeEach((to, from, next) => {
  const locale = to.params.locale || LocaleManager.activeLocale;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Auth screens should reject both active auth and auth that can be restored
  // through the refresh token, so fresh loads do not strand users on login.
  if (!requiresAuth) {
    if (to.name === 'login' || to.name === 'signup') {
      authService
        .restoreAuth()
        .then((isAuthenticated) => {
          if (isAuthenticated) {
            next({ name: 'pomodoro', params: { locale } });
            return;
          }
          next();
        })
        .catch(() => next());
      return;
    }
    next();
    return;
  }

  // Protected routes still enforce refresh when needed, because API calls in
  // those sections rely on a usable access token as soon as the view loads.
  authService
    .validateAndRefreshToken()
    .then(() => next())
    .catch(() =>
      next({
        name: 'login',
        params: { locale },
        query: buildLoginRedirectQuery(to)
      })
    );
});

export default router;
